import { emptyTrace, isKnownTraceEvent, reduceTrace } from "./reduceTrace";

class StreamingManager {
	constructor() {
		this.data = {};
		this.listeners = new Set();
	}

	subscribe(fn) {
		this.listeners.add(fn);
		return () => this.listeners.delete(fn);
	}

	notify() {
		for (const fn of this.listeners) fn(this.data);
	}

	/**
	 * Fold one `record_update_stream` message into the record's stream state.
	 * Runtime 08c: `trace` (labels-only, protocol 2) and `suggestions` ride the same message. (09b:
	 * Chat.jsx keeps `suggestions` per recordId itself and no longer passes them here — they can
	 * arrive after `done` has removed the entry.) A message carrying nothing this widget understands — an unknown field, an unknown
	 * trace kind — creates no entry, changes nothing and notifies nobody (D-L66); `prev` is returned.
	 */
	ingest({ recordId, conversationId, chunk, done, trace, suggestions }) {
		if (!recordId) return;
		const prev = this.data[recordId];
		const hasChunk = typeof chunk === "string" && chunk.length > 0;
		const hasTrace = isKnownTraceEvent(trace);
		const hasSuggestions = Array.isArray(suggestions);
		if (!hasChunk && !done && !hasTrace && !hasSuggestions) return prev;
		const base = prev || {
			recordId,
			conversationId,
			text: "",
			done: false,
			started: false,
			trace: emptyTrace(),
			suggestions: [],
		};
		const next = {
			...base,
			text: hasChunk ? base.text + chunk : base.text,
			done: !!done,
			started: base.started || hasChunk || hasTrace,
			trace: hasTrace ? reduceTrace(base.trace, trace) : base.trace,
			suggestions: hasSuggestions ? suggestions : base.suggestions,
		};
		this.data[recordId] = next;
		this.notify();
		return next;
	}

	/** Whether a reply of this conversation is visibly streaming (text or a live trace block). */
	hasLiveStream(conversationId) {
		return Object.values(this.data).some((s) => s.conversationId === conversationId && s.started && !s.done);
	}

	get(recordId) {
		return this.data[recordId];
	}

	getText(recordId) {
		return this.data[recordId]?.text || "";
	}

	isStreaming(recordId) {
		return !!this.data[recordId] && !this.data[recordId].done;
	}

	remove(recordId) {
		if (this.data[recordId]) {
			delete this.data[recordId];
			this.notify();
		}
	}

	clearConversation(conversationId) {
		let changed = false;
		Object.keys(this.data).forEach((id) => {
			if (this.data[id].conversationId === conversationId) {
				delete this.data[id];
				changed = true;
			}
		});
		if (changed) this.notify();
	}

	getAll() {
		return this.data;
	}
}

export { StreamingManager };
export const streamingManager = new StreamingManager();